import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Steps } from 'primereact/steps';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';
import GenericSurepassOutputComponent from '../output/GenericSurepassOutputComponent';

const FLOW_STEPS = [
    { label: 'Liveness Check' },
    { label: 'Face Match' },
];

const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

const FilePicker = ({ label, name, file, onChange, error, loading }) => {
    const inputRef = useRef(null);
    return (
        <div className="field col-12 md:col-6">
            <label className="block font-medium text-sm mb-2">
                {label} <span className="text-red-500">*</span>
            </label>
            <input ref={inputRef} type="file" accept="image/*" disabled={loading}
                onChange={(e) => onChange(name, e.target.files?.[0] || null)}
                style={{ display: 'none' }} />
            <div className={classNames('surface-100 border-round border-1 p-3 flex flex-column gap-2', error ? 'border-red-500' : 'surface-border')}>
                <div className="flex align-items-center justify-content-between gap-2">
                    <div className="flex align-items-center gap-2 min-w-0 flex-1">
                        <i className={`pi ${file ? 'pi-check-circle text-green-500' : 'pi-cloud-upload text-primary'}`} />
                        <span className="text-sm text-700 overflow-hidden text-overflow-ellipsis white-space-nowrap">
                            {file ? file.name : 'No file selected'}
                        </span>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                        <Button type="button" icon="pi pi-folder-open" label={file ? 'Change' : 'Choose'} size="small" outlined disabled={loading}
                            onClick={() => inputRef.current?.click()} />
                        {file && (
                            <Button type="button" icon="pi pi-times" size="small" severity="secondary" outlined aria-label={`Clear ${label}`}
                                disabled={loading} onClick={() => onChange(name, null)} />
                        )}
                    </div>
                </div>
                {file && (
                    <div className="flex align-items-center gap-2">
                        <Tag severity="info" value={file.type || 'image'} className="text-xs" />
                        <span className="text-xs text-500">{(file.size / 1024).toFixed(1)} KB</span>
                    </div>
                )}
                <span className="text-xs text-500">JPG / PNG up to 5 MB</span>
            </div>
            {error && (
                <small className="p-error block mt-1" role="alert">
                    <i className="pi pi-exclamation-circle mr-1" />{error}
                </small>
            )}
        </div>
    );
};

const FaceVerificationFlowComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [serviceDetails, setServiceDetails] = useState(null);
    const [fetchingService, setFetchingService] = useState(true);

    // Step 0 — Liveness
    const [livenessFile, setLivenessFile] = useState(null);
    const [livenessConsent, setLivenessConsent] = useState(false);
    const [livenessErrors, setLivenessErrors] = useState({});
    const [livenessResult, setLivenessResult] = useState(null);

    // Step 1 — Face Match
    const [matchFiles, setMatchFiles] = useState({ selfie: null, id_card: null });
    const [matchConsent, setMatchConsent] = useState(false);
    const [matchErrors, setMatchErrors] = useState({});
    const [matchResult, setMatchResult] = useState(null);

    useEffect(() => {
        if (!serviceId) { setFetchingService(false); return; }
        axiosInstance.get(`/user/report-services/${serviceId}`)
            .then((r) => { if (r.data.success) setServiceDetails(r.data.data); })
            .catch(() => setServiceDetails({ available_credits: 'N/A', credits_required: 'N/A' }))
            .finally(() => setFetchingService(false));
    }, [serviceId]);

    const showError = (error, summary) => {
        const s = error.response?.status;
        const msgs = { 500: 'Server error. Try again.', 422: 'Check your inputs.', 429: 'Too many requests.', 400: 'Invalid input.', 413: 'File too large.' };
        let detail = msgs[s] || error.response?.data?.message || error.message || summary;
        if (s === 402) {
            const c = error.response?.data || {};
            detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
        }
        toast.current?.show({ severity: 'error', summary, detail, life: 4000 });
    };

    // ── Step 0: Liveness ────────────────────────────────────────────────────
    const validateLiveness = () => {
        const next = {};
        if (!livenessFile) next.file = 'Face image is required';
        else if (livenessFile.size > MAX_SIZE) next.file = 'File exceeds 5 MB';
        if (!livenessConsent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const doLiveness = async () => {
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('file', livenessFile);
            fd.append('api_provider_service_id', String(parseInt(serviceId, 10)));
            const res = await axiosInstance.post('/surepass/face/face-liveness-v4', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (!res.data.success) throw new Error(res.data.message || 'Liveness check failed');
            setLivenessResult(res.data);
            toast.current?.show({ severity: 'success', summary: 'Liveness check complete', detail: 'Result is ready below', life: 2500 });
        } catch (err) {
            showError(err, 'Liveness check failed');
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleLivenessSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const errs = validateLiveness();
        if (Object.keys(errs).length) {
            setLivenessErrors(errs);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix highlighted fields', life: 3000 });
            return;
        }
        setLivenessErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">File:</span> <span className="text-900 font-medium">{livenessFile?.name}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits required:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm liveness check',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, check',
            rejectLabel: 'Cancel',
            accept: doLiveness,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    // ── Step 1: Face Match ───────────────────────────────────────────────────
    const validateMatch = () => {
        const next = {};
        if (!matchFiles.selfie) next.selfie = 'Selfie is required';
        else if (matchFiles.selfie.size > MAX_SIZE) next.selfie = 'File exceeds 5 MB';
        if (!matchFiles.id_card) next.id_card = 'ID card photo is required';
        else if (matchFiles.id_card.size > MAX_SIZE) next.id_card = 'File exceeds 5 MB';
        if (!matchConsent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const doFaceMatch = async () => {
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('selfie', matchFiles.selfie);
            fd.append('id_card', matchFiles.id_card);
            fd.append('api_provider_service_id', String(parseInt(serviceId, 10)));
            const res = await axiosInstance.post('/surepass/face/face-match', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (!res.data.success) throw new Error(res.data.message || 'Face match failed');
            setMatchResult(res.data);
            toast.current?.show({ severity: 'success', summary: 'Face match complete', detail: 'Result is ready below', life: 2500 });
        } catch (err) {
            showError(err, 'Face match failed');
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleMatchSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const errs = validateMatch();
        if (Object.keys(errs).length) {
            setMatchErrors(errs);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix highlighted fields', life: 3000 });
            return;
        }
        setMatchErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Selfie:</span> <span className="text-900 font-medium">{matchFiles.selfie?.name}</span></div>
                    <div className="mb-2"><span className="text-600">ID Card:</span> <span className="text-900 font-medium">{matchFiles.id_card?.name}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits required:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm face match',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, match',
            rejectLabel: 'Cancel',
            accept: doFaceMatch,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const setMatchFile = (name, file) => {
        setMatchFiles((p) => ({ ...p, [name]: file }));
        if (matchErrors[name]) setMatchErrors((p) => ({ ...p, [name]: '' }));
    };

    const resetFlow = () => {
        setStep(0);
        setLivenessFile(null);
        setLivenessConsent(false);
        setLivenessErrors({});
        setLivenessResult(null);
        setMatchFiles({ selfie: null, id_card: null });
        setMatchConsent(false);
        setMatchErrors({});
        setMatchResult(null);
        submittingRef.current = false;
    };

    const renderStep0 = () => (
        <div>
            {!livenessResult ? (
                <Card>
                    <form onSubmit={handleLivenessSubmit} noValidate>
                        <div className="grid">
                            <FilePicker label="Face Image" name="file" file={livenessFile}
                                onChange={(_, f) => { setLivenessFile(f); if (livenessErrors.file) setLivenessErrors((p) => ({ ...p, file: '' })); }}
                                error={livenessErrors.file} loading={loading} />
                        </div>
                        <ConsentCheckbox
                            checked={livenessConsent}
                            onChange={(e) => { setLivenessConsent(e.checked); if (livenessErrors.consent) setLivenessErrors((p) => ({ ...p, consent: '' })); }}
                            error={livenessErrors.consent}
                            reportType="face-liveness"
                        />
                        <div className="flex justify-content-end mt-3">
                            <Button type="submit" label="Run Liveness Check" icon="pi pi-arrow-right" iconPos="right" loading={loading} disabled={loading} size="small" />
                        </div>
                    </form>
                </Card>
            ) : (
                <>
                    <GenericSurepassOutputComponent reportData={livenessResult} title="Face Liveness Result" icon="pi-eye" />
                    <div className="flex justify-content-between mt-3">
                        <Button label="Redo Liveness" icon="pi pi-refresh" severity="secondary" outlined size="small"
                            onClick={() => { setLivenessResult(null); setLivenessFile(null); setLivenessConsent(false); }} />
                        <Button label="Continue to Face Match" icon="pi pi-arrow-right" iconPos="right" size="small" onClick={() => setStep(1)} />
                    </div>
                </>
            )}
        </div>
    );

    const renderStep1 = () => (
        <div>
            {!matchResult ? (
                <Card>
                    <form onSubmit={handleMatchSubmit} noValidate>
                        <div className="grid">
                            <FilePicker label="Selfie" name="selfie" file={matchFiles.selfie} onChange={setMatchFile} error={matchErrors.selfie} loading={loading} />
                            <FilePicker label="ID Card Photo" name="id_card" file={matchFiles.id_card} onChange={setMatchFile} error={matchErrors.id_card} loading={loading} />
                        </div>
                        <ConsentCheckbox
                            checked={matchConsent}
                            onChange={(e) => { setMatchConsent(e.checked); if (matchErrors.consent) setMatchErrors((p) => ({ ...p, consent: '' })); }}
                            error={matchErrors.consent}
                            reportType="face-match"
                        />
                        <div className="flex justify-content-between mt-3">
                            <Button type="button" label="Back to Liveness" icon="pi pi-arrow-left" severity="secondary" outlined size="small" onClick={() => setStep(0)} disabled={loading} />
                            <Button type="submit" label="Match Faces" icon="pi pi-arrow-right" iconPos="right" loading={loading} disabled={loading} size="small" />
                        </div>
                    </form>
                </Card>
            ) : (
                <>
                    <GenericSurepassOutputComponent reportData={matchResult} title="Face Match Result" icon="pi-users" />
                    <div className="flex justify-content-between mt-3">
                        <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined size="small" onClick={() => setStep(0)} />
                        <Button label="Start Over" icon="pi pi-refresh" outlined size="small" onClick={resetFlow} />
                    </div>
                </>
            )}
        </div>
    );

    const stepRenderers = [renderStep0, renderStep1];

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-eye"
                        title="Face Verification Flow"
                        subtitle="Liveness check followed by face match — complete both verifications in one flow"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    <Card className="mb-3">
                        <Steps model={FLOW_STEPS} activeIndex={step} readOnly className="mb-0" />
                    </Card>
                    {stepRenderers[step] && stepRenderers[step]()}
                </div>
            </div>
        </>
    );
};

export default FaceVerificationFlowComponent;
